/* Sorenzia launch fixes (12 Oct 2026)
   1. Product page: the shopper must choose a size before Add to bag / sticky bar / express checkout.
   2. Product page: "Size guide" opens the size guide sheet on the size chart.
   3. Bag and cart page: change the size of a line, and a size guide for each line. */
(() => {
  if (window.__szLaunch) return;
  window.__szLaunch = true;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Require an intentional size choice ----------
     The theme pre-selects the first size. We clear the size (colour stays) and block buying until a
     size is picked. Landings from Google Shopping keep the advertised variant selected, as Merchant
     Center expects the landing page to show the item that was clicked. */
  const onProductPage = /\/products\//.test(location.pathname);
  const q = new URLSearchParams(location.search);
  const fromGoogle =
    q.has('variant') &&
    (['gclid', 'gbraid', 'wbraid', 'srsltid'].some((k) => q.has(k)) || /google/i.test(q.get('utm_source') || ''));

  if (onProductPage && !fromGoogle) {
    let chosen = false;
    const isSize = (el) => /size/i.test(el.dataset.optionName || el.dataset.szStickyOption || '');
    const mainPicker = () =>
      [...document.querySelectorAll('variant-picker[data-template-product-match="true"]')].find(
        (p) => !p.closest('sticky-add-to-cart, dialog, quick-add-dialog')
      );
    const sectionOf = () => mainPicker()?.closest('.shopify-section') || null;
    const sizeRadios = () => {
      const p = mainPicker();
      return p ? [...p.querySelectorAll('input[type="radio"][data-option-name]')].filter(isSize) : [];
    };

    const apply = () => {
      if (chosen) return;
      const radios = sizeRadios();
      if (!radios.length) return;
      const sec = sectionOf();
      if (sec && !sec.classList.contains('sz-need-size')) sec.classList.add('sz-need-size');
      radios.forEach((r) => {
        if (r.checked) r.checked = false;
        if (r.dataset.currentChecked !== 'false') r.dataset.currentChecked = 'false';
      });
      sec?.querySelectorAll('select[data-sz-sticky-option]').forEach((s) => {
        if (!isSize(s)) return;
        if (!s.querySelector('option[data-sz-placeholder]')) {
          const o = document.createElement('option');
          o.value = '';
          o.textContent = 'Choose size';
          o.disabled = true;
          o.dataset.szPlaceholder = '';
          s.prepend(o);
        }
        if (s.value !== '') s.value = '';
      });
    };

    const release = () => {
      if (chosen) return;
      chosen = true;
      sectionOf()?.classList.remove('sz-need-size');
      document.querySelectorAll('.sz-size-msg').forEach((m) => (m.hidden = true));
      document.querySelectorAll('option[data-sz-placeholder]').forEach((o) => o.remove());
    };

    const promptSize = () => {
      const first = sizeRadios()[0];
      const fs = first?.closest('fieldset');
      if (!fs) return;
      let m = fs.querySelector('.sz-size-msg');
      if (!m) {
        m = document.createElement('p');
        m.className = 'sz-size-msg';
        m.setAttribute('role', 'alert');
        m.textContent = 'Please choose your size';
        const legend = fs.querySelector('legend');
        if (legend) legend.after(m);
        else fs.prepend(m);
      }
      m.hidden = false;
      fs.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
      const target = fs.querySelector('input:not([aria-disabled="true"])') || first;
      setTimeout(() => target.focus({ preventScroll: true }), reduceMotion() ? 0 : 450);
    };

    // Any size picked in the main picker, the sticky bar or the size finder releases the lock.
    document.addEventListener(
      'change',
      (e) => {
        const t = e.target;
        if (chosen || !(t instanceof Element)) return;
        if (t.matches('input[type="radio"][data-option-name]') && isSize(t) && t.closest('variant-picker') === mainPicker()) release();
        else if (t.matches('select[data-sz-sticky-option]') && isSize(t) && t.value) release();
      },
      true
    );

    const BUY = 'button[name="add"], [ref="addToCartButton"], .shopify-payment-button, shopify-accelerated-checkout';
    const block = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      promptSize();
    };
    document.addEventListener(
      'click',
      (e) => {
        if (chosen) return;
        const t = e.target;
        if (!(t instanceof Element) || !t.closest(BUY)) return;
        const sec = sectionOf();
        if (sec && sec.contains(t) && sizeRadios().length) block(e);
      },
      true
    );
    document.addEventListener(
      'submit',
      (e) => {
        if (chosen) return;
        const f = e.target;
        const sec = sectionOf();
        if (f instanceof HTMLFormElement && sec && sec.contains(f) && /\/cart\/add/.test(f.action) && sizeRadios().length) block(e);
      },
      true
    );

    // Horizon re-renders the picker when the colour changes: clear the size again until one is chosen.
    let pending = 0;
    new MutationObserver(() => {
      if (chosen || pending) return;
      pending = requestAnimationFrame(() => {
        pending = 0;
        apply();
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply);
    else apply();
  }

  /* ---------- 2. "Size guide" opens the sheet on the size chart ---------- */
  document.addEventListener(
    'click',
    (e) => {
      const link = e.target instanceof Element ? e.target.closest('[data-sz-size-guide-open]') : null;
      if (!link) return;
      const dialog = document.getElementById(`SzSizeGuide-${link.dataset.szSizeGuideOpen}`);
      if (!dialog || !dialog.querySelector('[data-sz-pane="chart"]')) return; // fall back to the old behaviour
      e.preventDefault();
      e.stopImmediatePropagation();
      dialog.querySelectorAll('[role="tab"][data-sz-tab]').forEach((b) => {
        const on = b.dataset.szTab === 'chart';
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on);
      });
      dialog.querySelectorAll('[data-sz-pane]').forEach((p) => {
        const on = p.dataset.szPane === 'chart';
        p.classList.toggle('is-active', on);
        p.hidden = !on;
      });
      dialog._szTrigger = link;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add('sz-scroll-lock');
      requestAnimationFrame(() => dialog.classList.add('is-open'));
    },
    true
  );

  /* ---------- 3. Bag and cart page: change size + size guide per line ---------- */
  const productCache = new Map();
  const chartCache = new Map();
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const getProduct = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${root}products/${handle}.js`, { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null))
      );
    }
    return productCache.get(handle);
  };
  const getChart = (handle) => {
    if (!chartCache.has(handle)) {
      chartCache.set(
        handle,
        fetch(`${root}products/${handle}`)
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return [...doc.querySelectorAll('.sz-sg__data [data-sz-row]')].map((s) => ({
              uk: s.dataset.uk,
              eu: s.dataset.eu,
              cm: s.dataset.cm,
            }));
          })
          .catch(() => [])
      );
    }
    return chartCache.get(handle);
  };
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  let cartPromise = null;
  const getCart = () => {
    if (!cartPromise) {
      cartPromise = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .finally(() => setTimeout(() => (cartPromise = null), 300));
    }
    return cartPromise;
  };

  const working = new WeakSet();
  async function enhanceRow(row, cart) {
    if (working.has(row) || row.querySelector('[data-sz-line-size]')) return;
    working.add(row);
    try {
      const item = cart.items.find((i) => i.key === row.dataset.key);
      if (!item || !item.handle) return;
      const product = await getProduct(item.handle);
      if (!product || !row.isConnected || row.querySelector('[data-sz-line-size]')) return;
      const names = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
      const sizeIdx = names.findIndex((n) => /size/i.test(n));
      if (sizeIdx < 0) return;
      const current = product.variants.find((v) => v.id === item.variant_id);
      if (!current) return;
      const same = product.variants.filter((v) => v.options.every((o, j) => j === sizeIdx || o === current.options[j]));
      if (same.length < 2) return;

      const select = document.createElement('select');
      select.className = 'sz-line-size__select';
      select.dataset.szLineSize = '';
      select.dataset.lineKey = item.key;
      select.dataset.variantId = String(item.variant_id);
      select.dataset.qty = String(item.quantity);
      select.setAttribute('aria-label', `${names[sizeIdx]} for ${item.product_title}`);
      select.innerHTML = same
        .map(
          (v) =>
            `<option value="${v.id}"${v.id === current.id ? ' selected' : ''}${v.available ? '' : ' disabled'}>${esc(v.options[sizeIdx])}${
              v.available ? '' : ' – sold out'
            }</option>`
        )
        .join('');

      // Put the select where the size text is; otherwise just after the variant line.
      const sizeText = current.options[sizeIdx];
      const dd = [...row.querySelectorAll('.cart-items__variants dd')].find((d) => d.textContent.replace(/[\s, ]+$/, '').trim() === sizeText);
      if (dd) {
        dd.textContent = '';
        dd.appendChild(select);
        dd.closest('.cart-items__variant')?.classList.add('sz-line-size');
      } else {
        const holder = document.createElement('div');
        holder.className = 'sz-line-size';
        holder.appendChild(select);
        (row.querySelector('.cart-items__variants') || row.querySelector('.cart-items__details'))?.after(holder);
      }

      // Size guide (loaded when opened)
      const guide = document.createElement('details');
      guide.className = 'sz-line-guide';
      guide.innerHTML = '<summary class="sz-line-guide__toggle">Size guide</summary><div class="sz-line-guide__body"><p class="sz-line-guide__loading">Loading…</p></div>';
      guide.addEventListener('toggle', async () => {
        if (!guide.open || guide.dataset.loaded) return;
        guide.dataset.loaded = '1';
        const rows = await getChart(item.handle);
        const body = guide.querySelector('.sz-line-guide__body');
        if (!rows.length) {
          body.innerHTML = `<p><a href="${esc(item.url)}">See the size guide on the product page</a></p>`;
          return;
        }
        body.innerHTML = `<table class="sz-line-guide__table"><caption class="visually-hidden">Size chart for ${esc(item.product_title)}</caption><thead><tr><th scope="col">UK</th><th scope="col">EU</th><th scope="col">Foot length</th></tr></thead><tbody>${rows
          .map((r) => `<tr><th scope="row">UK ${esc(r.uk)}</th><td>${esc(r.eu)}</td><td>${esc(r.cm)} cm</td></tr>`)
          .join('')}</tbody></table><p class="sz-line-guide__how">Foot length: back of the heel to the tip of the longest toe.</p>`;
      });
      const anchor = row.querySelector('.cart-items__variants-wrapper') || select.closest('.sz-line-size');
      anchor?.appendChild(guide);
    } catch (err) {
      console.warn('[sz-launch] line size', err);
    } finally {
      working.delete(row);
    }
  }

  let scanTimer = 0;
  const scan = () => {
    if (scanTimer) return;
    scanTimer = setTimeout(async () => {
      scanTimer = 0;
      const rows = [...document.querySelectorAll('.cart-items__table-row[data-key]')].filter(
        (r) => !r.querySelector('[data-sz-line-size]') && !working.has(r)
      );
      if (!rows.length) return;
      try {
        const cart = await getCart();
        rows.forEach((r) => enhanceRow(r, cart));
      } catch (err) {
        console.warn('[sz-launch] cart', err);
      }
    }, 80);
  };
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();

  document.addEventListener('change', async (e) => {
    const sel = e.target instanceof Element ? e.target.closest('[data-sz-line-size]') : null;
    if (!sel) return;
    const key = sel.dataset.lineKey;
    const id = sel.value;
    const qty = parseInt(sel.dataset.qty, 10) || 1;
    if (!key || !id) return;
    const row = sel.closest('.cart-items__table-row');
    sel.disabled = true;
    row?.classList.add('sz-line-busy');
    try {
      const actions = window.Shopify && window.Shopify.actions;
      if (actions && typeof actions.updateCart === 'function') {
        // Add the new size first, then remove the old line, so a failed add never loses the pair.
        const added = await actions.updateCart(
          { lines: [{ merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity: qty }] },
          { event: { context: 'cart' } }
        );
        if (added && added.userErrors && added.userErrors.length) throw new Error(added.userErrors[0].message);
        // Line keys change after every cart update, so look the old line up again by its variant.
        const oldVariant = Number(sel.dataset.variantId);
        const fresh = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const old = fresh.items.find((i) => i.variant_id === oldVariant);
        if (old) {
          const removed = await actions.updateCart({ lines: [{ id: old.key, quantity: 0 }] }, { event: { context: 'cart' } });
          if (removed && removed.userErrors && removed.userErrors.length) throw new Error(removed.userErrors[0].message);
        }
        setTimeout(() => {
          if (row && row.isConnected && row.classList.contains('sz-line-busy')) window.location.reload();
        }, 4000);
      } else {
        const res = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: { [key]: 0, [id]: qty } }),
        });
        if (!res.ok) throw new Error('update failed');
        window.location.reload();
      }
    } catch (err) {
      console.warn('[sz-launch] change size', err);
      sel.disabled = false;
      row?.classList.remove('sz-line-busy');
      let m = row?.querySelector('.sz-line-size__err');
      if (row && !m) {
        m = document.createElement('p');
        m.className = 'sz-line-size__err';
        m.setAttribute('role', 'alert');
        sel.closest('.cart-items__variants, .sz-line-size')?.after(m);
      }
      if (m) m.textContent = 'Sorry, we could not change the size. Please try again.';
    }
  });
})();
